<script setup lang="ts">
/**
 * The shell: navigation, the period picker, and which screen is showing.
 *
 * It composes and routes; it decides nothing about money. Every figure arrives
 * already folded by the API and already parsed to `bigint` at the client
 * boundary, so there is no arithmetic in this file to get wrong.
 *
 * IT USED TO CARRY AN UNSCOPED STYLE BLOCK at the bottom, holding the `gf-*`
 * classes that nine screens share. The reasoning was right — one table, one
 * panel and one chip, rather than nine copies that drift apart by the third
 * screen — and the address was wrong. It lived inside one app's component,
 * where the till, the kitchen screen and the guest's phone could not reach it,
 * and between them they had 23 more definitions of a primary button.
 *
 * It is `@gf/ui/office.css` now, imported by `main.ts`. Four of the nine screens
 * here still have no `<style>` block of their own and re-skin from it.
 */

import { computed, defineAsyncComponent, nextTick, onMounted, onBeforeUnmount, ref } from "vue";
import { GfIcon } from "@gf/ui";
import type { CheckDetail, CheckSummary, ConsoleApi, FiscalState, KitchenTimings, SalesReport, ShiftReport } from "../api";
import type { Loaded, RangeName } from "../load";
import type { LocaleName, MessageKey } from "../locales";
import { navFor, type ScreenId } from "../nav";
import { SKETCHES } from "../preview/sketches";
import SketchScreen from "../preview/SketchScreen.vue";
import LoadState from "./LoadState.vue";
import TodayScreen from "./TodayScreen.vue";
import SetupChecklist from "./SetupChecklist.vue";
import SalesScreen from "./SalesScreen.vue";
import BreakdownPanel from "./BreakdownPanel.vue";
import ChecksScreen from "./ChecksScreen.vue";
import ShiftsScreen from "./ShiftsScreen.vue";
import KitchenScreen from "./KitchenScreen.vue";
import FiscalScreen from "./FiscalScreen.vue";
import MenuScreen from "./MenuScreen.vue";
import StaffScreen from "./StaffScreen.vue";
import VenueScreen from "./VenueScreen.vue";
import DevicesPanel from "./DevicesPanel.vue";
import PohodaExport from "./PohodaExport.vue";
import PohodaPanel from "./PohodaPanel.vue";
import ModulesPanel from "./ModulesPanel.vue";
import SettingsScreen from "./SettingsScreen.vue";
import TableCardsScreen from "./TableCardsScreen.vue";

/**
 * Lazy, and the only lazily-imported thing in this app.
 *
 * The back office has no bundle budget — it is a manager's laptop on a desk
 * with a network — but a palette nobody has opened yet should not be parsed
 * before the first screen paints. It is also the only component here that is
 * reached by a keystroke rather than by a click, which makes it the only one
 * whose absence on first paint nobody can notice.
 */
const CommandPalette = defineAsyncComponent(() => import("./CommandPalette.vue"));

/**
 * Sklad, lazily too: a venue without the stock module never parses it, and
 * one with it opens it a few times a week, not on every visit.
 */
const StockScreen = defineAsyncComponent(() => import("./StockScreen.vue"));
const ReservationsScreen = defineAsyncComponent(() => import("./ReservationsScreen.vue"));
const HistoryScreen = defineAsyncComponent(() => import("./HistoryScreen.vue"));
const PrintersScreen = defineAsyncComponent(() => import("./PrintersScreen.vue"));
const StockLowNote = defineAsyncComponent(() => import("./stock/StockLowNote.vue"));
const ApprovalFlagsNote = defineAsyncComponent(() => import("./ApprovalFlagsNote.vue"));
const PublicPagePanel = defineAsyncComponent(() => import("./PublicPagePanel.vue"));

const props = defineProps<{
  screen: ScreenId;
  range: RangeName;
  /** The "Připravujeme" drawings: the demo's, never a venue's own office. */
  previews?: boolean;
  /** The modules in effect at the venue; a screen for any other is not in the nav. Absent: all show. */
  modules?: readonly string[];
  /** The period chosen by hand, when `range` is "custom". */
  customRange?: { readonly from: string; readonly to: string };
  /** The venue's trading day, by its own clock and cutover. */
  today: string;
  locale: LocaleName;
  t: (key: string) => string;
  venueName: string;
  timezone: string;
  /** The signed-in venue, for what is remembered per venue. */
  venueId: string;
  venue: {
    name: string;
    timezone: string;
    businessDayStartMinutes: number;
    fiscalRegistrationId: string | null;
  } | null;
  devices: readonly { deviceId: string; name: string; fiscalTillId: string | null }[];
  staff: readonly { staffId: string; displayName: string; roleId: string }[];
  sales: Loaded<SalesReport>;
  checks: Loaded<CheckSummary[]>;
  detail: Loaded<CheckDetail> | null;
  shifts: Loaded<ShiftReport[]>;
  kitchen: Loaded<KitchenTimings>;
  fiscal: Loaded<FiscalState>;
  /** The client, handed to the screens that load and write for themselves. */
  api: ConsoleApi | null;
  gaps: readonly Record<string, unknown>[];
  issued: number;
  /** Who is signed in. Null only for the moment before the first render. */
  signedInAs: { displayName: string; roleId: string } | null;
  /** The cards last printed, if any. Not loaded until somebody asks. */
  tableCards: TableCards | null;
  tableCardsBusy: boolean;
  tableCardsNotice: string | null;
}>();

const emit = defineEmits<{
  navigate: [screen: ScreenId];
  range: [value: RangeName];
  customRange: [value: { from: string; to: string }];
  locale: [value: LocaleName];
  openCheck: [orderId: string];
  closeCheck: [];
  retry: [];
  signOut: [];
  printTableCards: [tables: readonly string[]];
  modulesChanged: [effective: readonly string[]];
}>();

const labels = computed(() => ({
  loading: props.t("common.loading"),
  failed: props.t("common.failed"),
  retry: props.t("common.retry"),
  unreachable: props.t("common.unreachable"),
}));

const RANGES: readonly RangeName[] = ["today", "week", "month", "custom"];

const rangeLabel = (name: RangeName): string =>
  props.t(name === "today" ? "common.today" : name === "week" ? "common.week" : name === "month" ? "common.month" : "common.custom");

const nav = computed(() => navFor(props.previews ?? false, props.modules));

const title = computed(() => {
  for (const group of nav.value) {
    for (const item of group.items) {
      if (item.id === props.screen) return props.t(item.label);
    }
  }
  return props.t("app.title");
});

/** The period picker is meaningless on screens that have no period. */
const showsRange = computed(() =>
  ["sales", "checks", "shifts", "kitchen"].includes(props.screen),
);

const sketch = computed(() => SKETCHES[props.screen] ?? null);

// --- The command palette ---------------------------------------------------

/**
 * ⌘ on an Apple keyboard, Ctrl on every other one.
 *
 * The handler accepts both — `metaKey || ctrlKey` — so the shortcut has always
 * worked everywhere. What was wrong was the LABEL: a button reading "⌘K" on a
 * Windows till-office laptop is telling somebody to press a key their keyboard
 * does not have, and the glyph is not on most Windows fonts either.
 *
 * `navigator.platform` is deprecated but is the only synchronous answer;
 * `userAgentData` is behind a promise and not on Safari. Read once, at module
 * scope, because it cannot change while the page is open.
 */
const APPLE = /Mac|iPhone|iPad|iPod/.test(
  navigator.platform || navigator.userAgent,
);
const shortcutLabel = APPLE ? "\u2318K" : "Ctrl K";

const paletteOpen = ref(false);

/**
 * ⌘K on a Mac, Ctrl-K everywhere else — `metaKey || ctrlKey` rather than
 * sniffing the platform, because the wrong guess is a shortcut that silently
 * does nothing on somebody's machine.
 *
 * `preventDefault` matters: Ctrl-K is Firefox's own search-bar focus and ⌘K is
 * a link shortcut in some editors embedded in a browser. A palette that opens
 * AND moves focus to the browser chrome is worse than no palette.
 *
 * Captured on `window` rather than on a focusable element, because the point is
 * that it works from wherever the manager happens to be.
 */
function onShortcut(event: KeyboardEvent): void {
  if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
  event.preventDefault();
  paletteOpen.value = !paletteOpen.value;
}

// --- The navigation on a phone ---------------------------------------------

/**
 * BELOW 60rem THE SIDEBAR IS A SHEET, not a stack.
 *
 * It used to become a static block ABOVE the screen — about 800 px of
 * navigation on a 844 px phone, so every tap landed a manager on a page whose
 * content started below the fold (the 2026-09-25 tour, `office-390-*`). Now a
 * 48 px bar carries a menu button, the screen's name and search, and the same
 * `<nav>` slides over the page from the left when asked. One element, two
 * presentations: the desktop never sees the button, the phone never sees the
 * column.
 *
 * While it is open it behaves as a modal: Tab stays inside it, Escape closes
 * it, and focus goes back to the button that opened it. Choosing a screen
 * closes it, because that is what somebody who opened a menu on a phone wants.
 */
const NARROW = "(max-width: 60rem)";
const navOpen = ref(false);
const navEl = ref<HTMLElement | null>(null);
const menuButton = ref<HTMLButtonElement | null>(null);

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function openNav(): void {
  navOpen.value = true;
  void nextTick(() => {
    const current = navEl.value?.querySelector<HTMLElement>('[aria-current="page"]');
    (current ?? navEl.value?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
  });
}

function closeNav(restoreFocus = true): void {
  if (!navOpen.value) return;
  navOpen.value = false;
  if (restoreFocus) void nextTick(() => menuButton.value?.focus());
}

function go(screen: ScreenId): void {
  closeNav(false);
  emit("navigate", screen);
}

function openPalette(): void {
  closeNav(false);
  paletteOpen.value = true;
}

function onNavKeydown(event: KeyboardEvent): void {
  if (!navOpen.value || !window.matchMedia(NARROW).matches) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeNav();
    return;
  }
  if (event.key !== "Tab" || !navEl.value) return;
  const focusables = [...navEl.value.querySelectorAll<HTMLElement>(FOCUSABLE)];
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/** Widening the window past the breakpoint with the sheet open must not leave a scrim behind. */
let narrowQuery: MediaQueryList | null = null;
const onBreakpoint = (event: MediaQueryListEvent): void => {
  if (!event.matches) navOpen.value = false;
};

onMounted(() => {
  window.addEventListener("keydown", onShortcut);
  narrowQuery = window.matchMedia(NARROW);
  narrowQuery.addEventListener("change", onBreakpoint);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onShortcut);
  narrowQuery?.removeEventListener("change", onBreakpoint);
});

const todayFacts = computed(() =>
  props.sales.status === "ready" && props.checks.status === "ready" && props.fiscal.status === "ready"
    ? { sales: props.sales.value, checks: props.checks.value, fiscal: props.fiscal.value }
    : null,
);

/** Today's three requests, as ONE state — it is one screen, so it is one answer. */
const todayState = computed<Loaded<unknown>>(() => {
  for (const part of [props.sales, props.checks, props.fiscal]) {
    if (part.status === "failed") return part;
  }
  return todayFacts.value === null ? { status: "loading" } : { status: "ready", value: null };
});
</script>

<template>
  <div class="shell" :class="{ 'shell--navOpen': navOpen }">
    <!-- Phone only: the page behind the open navigation sheet. -->
    <div v-if="navOpen" class="shell__scrim" aria-hidden="true" @click="closeNav()"></div>

    <nav id="shell-nav" ref="navEl" class="shell__nav" :aria-label="t('app.title')" @keydown="onNavKeydown">
      <div class="shell__brand">
        <img src="/icon.svg" alt="" width="24" height="24" />
        <div class="shell__brandText">
          <strong class="shell__venue">{{ venueName }}</strong>
          <span class="shell__product">GastroFirst</span>
        </div>
        <!-- Phone only: the sheet's own way out, for a thumb that will not reach the scrim. -->
        <button type="button" class="shell__navClose" :aria-label="t('shell.closeMenu')" @click="closeNav()">
          <GfIcon name="x" :size="18" />
        </button>
      </div>

      <!--
        A VISIBLE TRIGGER for a keyboard feature.

        A shortcut nobody is told about is a shortcut for the person who
        built it. The button carries the keystroke on its face, which is how
        somebody learns it without being taught. In the sidebar, where
        MyMonday keeps it, so it is in the same place on every screen.
      -->
      <button type="button" class="shell__palette" @click="openPalette">
        <GfIcon name="search" class="shell__icon" />
        <span class="shell__paletteLabel">{{ t("palette.open") }}</span>
        <kbd class="gf-kbd">{{ shortcutLabel }}</kbd>
      </button>

      <div class="shell__groups">
        <div v-for="group in nav" :key="group.label" class="shell__groupBlock">
          <p class="shell__group">{{ t(group.label) }}</p>
          <ul class="shell__items">
            <li v-for="item in group.items" :key="item.id">
              <!--
                The icon comes first and carries no text, so the button's text
                still STARTS with its label — `tools/rehearse/src/cli.ts` finds
                "Menu" by `/^Menu/`, and a glyph in front would break it.
              -->
              <!--
                A preview says so under its GROUP's heading ("Připravujeme"),
                and to a screen reader on the item itself. The per-item pill
                repeated the heading six times and cut "Faktury od
                dodavatelů" down to "Faktury od d…".
              -->
              <button
                type="button"
                class="shell__item"
                :class="item.preview ? 'shell__item--preview' : ''"
                :aria-current="screen === item.id ? 'page' : undefined"
                @click="go(item.id)"
              >
                <GfIcon :name="item.icon" class="shell__icon" />
                <span class="shell__label">{{ t(item.label) }}</span>
                <span v-if="item.preview" class="gf-sr">— {{ t("preview.badge") }}</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!--
        Who is signed in, and the way out. At the foot of the sidebar rather
        than buried in Settings: on a shared office laptop the useful question
        is "am I still signed in as the manager", and it should be answerable
        at a glance from any screen.
      -->
      <div v-if="signedInAs" class="shell__who">
        <span class="shell__whoName">
          {{ t("signIn.signedInAs") }} <strong>{{ signedInAs.displayName }}</strong>
        </span>
        <button type="button" class="shell__signOut" @click="$emit('signOut')">
          <GfIcon name="logOut" class="shell__icon" />
          <span>{{ t("signIn.signOut") }}</span>
        </button>
      </div>
    </nav>

    <main class="shell__main">
      <!--
        MONDAY'S PAGE BAR: one quiet line, the venue then the screen. The
        heading is small on purpose — the figures below are what a manager
        came for, and a 30 px title above them was the loudest thing on the
        page. `main h1` still names the screen; the office rehearsal reads it.
      -->
      <header class="shell__header">
        <!-- Phone only: the navigation lives behind this. -->
        <button
          ref="menuButton"
          type="button"
          class="shell__barButton shell__menuButton"
          :aria-label="t('shell.menu')"
          aria-controls="shell-nav"
          :aria-expanded="navOpen ? 'true' : 'false'"
          @click="openNav"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div class="shell__crumbs">
          <span class="shell__crumb">{{ venueName }}</span>
          <span class="shell__sep" aria-hidden="true">/</span>
          <h1 class="shell__title">{{ title }}</h1>
        </div>

        <!-- Phone only: search, where the sidebar's trigger cannot be seen. -->
        <button type="button" class="shell__barButton shell__searchButton" :aria-label="t('palette.open')" @click="openPalette">
          <GfIcon name="search" :size="18" />
        </button>

        <div v-if="showsRange" class="shell__tools">
          <!-- A period of one's own: last month, a festival weekend. -->
          <div v-if="range === 'custom'" class="shell__custom">
            <input
              type="date"
              class="gf-input shell__date"
              :aria-label="t('common.from')"
              :max="today"
              :value="customRange?.from"
              data-field="rangeFrom"
              @change="$emit('customRange', { from: ($event.target as HTMLInputElement).value, to: customRange?.to ?? today })"
            />
            <span class="gf-muted">–</span>
            <input
              type="date"
              class="gf-input shell__date"
              :aria-label="t('common.to')"
              :max="today"
              :value="customRange?.to"
              data-field="rangeTo"
              @change="$emit('customRange', { from: customRange?.from ?? today, to: ($event.target as HTMLInputElement).value })"
            />
          </div>

          <div class="shell__range" role="group" :aria-label="t('common.date')">
            <button
              v-for="name in RANGES"
              :key="name"
              type="button"
              class="shell__rangeOption"
              :aria-pressed="range === name"
              @click="$emit('range', name)"
            >
              {{ rangeLabel(name) }}
            </button>
          </div>
        </div>
      </header>

      <CommandPalette
        v-if="paletteOpen"
        :t="t"
        :current="screen"
        :previews="previews ?? false"
        @navigate="$emit('navigate', $event)"
        @close="paletteOpen = false"
      />

      <!--
        THE SCREEN'S OWN CONTENT, marked so a test can ask for it.

        `tools/rehearse/src/office.ts` used to isolate this by reading all of
        `main` and splitting the string on the literal text
        "PŘIPRAVUJEME Věrnost PŘIPRAVUJEME" — the last item in the sidebar —
        to throw away the navigation. The navigation is not inside `main`, so
        that split never matched anything and the guard was doing nothing at
        all; it would have started mattering, wrongly, the first time a label
        changed. What DID need excluding is this header, which carries the
        breadcrumb and the period buttons and would otherwise count as screen
        content.
      -->
      <div class="shell__content" data-screen>

        <SketchScreen v-if="sketch" :sketch="sketch" :t="t" />

        <LoadState
          v-else-if="screen === 'today'"
          :state="todayState"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <SetupChecklist v-if="api && venueId" :api="api" :venue-id="venueId" :t="t" @navigate="$emit('navigate', $event)" />
          <StockLowNote v-if="api && modules?.includes('stock')" :api="api" :t="t" :locale="locale" @open="$emit('navigate', 'stock')" />
          <ApprovalFlagsNote v-if="api" :api="api" :t="t" :locale="locale" :timezone="venue?.timezone ?? timezone" @open="(id) => $emit('openCheck', id)" />
          <TodayScreen
            v-if="todayFacts"
            :sales="todayFacts.sales"
            :checks="todayFacts.checks"
            :fiscal="todayFacts.fiscal"
            :fiscal-configured="venue ? venue.fiscalRegistrationId !== null : true"
            :locale="locale"
            :t="t"
          />
        </LoadState>

        <LoadState
          v-else-if="screen === 'sales'"
          :state="sales"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <SalesScreen
            v-if="sales.status === 'ready'"
            :report="sales.value"
            :locale="locale"
            :t="t"
          />
          <BreakdownPanel
            v-if="sales.status === 'ready' && api"
            :api="api"
            :from="sales.value.from"
            :to="sales.value.to"
            :locale="locale"
            :t="t"
            :staff-names="Object.fromEntries(staff.map((p) => [p.staffId, p.displayName]))"
          />
          <PohodaExport v-if="sales.status === 'ready' && api" :api="api" :today="today" :locale="locale" :t="t" />
        </LoadState>

        <LoadState
          v-else-if="screen === 'checks'"
          :state="checks"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <ChecksScreen
            v-if="checks.status === 'ready'"
            :checks="checks.value"
            :detail="detail"
            :today="today"
            :timezone="timezone"
            :locale="locale"
            :t="t"
            @open="(id) => $emit('openCheck', id)"
            @close="$emit('closeCheck')"
          />
        </LoadState>

        <LoadState
          v-else-if="screen === 'shifts'"
          :state="shifts"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <ShiftsScreen
            v-if="shifts.status === 'ready'"
            :shifts="shifts.value.shifts"
            :unattributed="shifts.value.unattributed"
            :timezone="timezone"
            :locale="locale"
            :t="t"
          />
        </LoadState>

        <LoadState
          v-else-if="screen === 'kitchen'"
          :state="kitchen"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <KitchenScreen
            v-if="kitchen.status === 'ready'"
            :timings="kitchen.value"
            :timezone="timezone"
            :locale="locale"
            :t="t"
          />
        </LoadState>

        <LoadState
          v-else-if="screen === 'fiscal'"
          :state="fiscal"
          :labels="labels"
          @retry="$emit('retry')"
        >
          <FiscalScreen
            v-if="fiscal.status === 'ready'"
            :fiscal="fiscal.value"
            :gaps="gaps"
            :issued="issued"
            :timezone="timezone"
            :locale="locale"
            :t="t"
          />
        </LoadState>

        <MenuScreen v-else-if="screen === 'menu' && api" :api="api" :locale="locale" :t="t" />

        <StaffScreen v-else-if="screen === 'staff' && api" :api="api" :me="signedInAs" :t="t" />

        <PrintersScreen
          v-else-if="screen === 'printers' && api"
          :api="api"
          :t="t"
          :locale="locale"
          :timezone="venue?.timezone ?? timezone"
          :can-set-up="signedInAs?.roleId === 'owner' || signedInAs?.roleId === 'admin'"
        />

        <HistoryScreen
          v-else-if="screen === 'history' && api"
          :api="api"
          :t="t"
          :locale="locale"
          :timezone="venue?.timezone ?? timezone"
        />

        <ReservationsScreen
          v-else-if="screen === 'reservations' && api"
          :api="api"
          :t="t"
          :locale="locale"
          :timezone="venue?.timezone ?? timezone"
        />

        <StockScreen
          v-else-if="screen === 'stock' && api"
          :api="api"
          :me="signedInAs"
          :t="t"
          :locale="locale"
          :timezone="venue?.timezone ?? timezone"
        />

        <!--
          NO `LoadState` AROUND IT, and that is deliberate.
          Nothing is fetched until somebody types table names and presses the
          button, so there is no loading state to be in — a spinner on arrival
          would be a spinner for a request nobody made.
        -->
        <TableCardsScreen
          v-else-if="screen === 'tableCards'"
          :t="t"
          :locale="locale"
          :cards="tableCards"
          :busy="tableCardsBusy"
          :notice="tableCardsNotice"
          :session-mode="tableCards?.sessionMode ?? null"
          @print="(tables) => $emit('printTableCards', tables)"
        />

        <!--
          TWO NAV ITEMS, TWO SCREENS — it used to be one.
          `SettingsScreen` was the `v-else` catch-all, so "Lidé" and "Nastavení"
          rendered the same page and clicking People showed timezone settings with
          the roster somewhere below the fold.
        -->
        <template v-else-if="screen === 'settings' && api">
          <ModulesPanel :api="api" :me="signedInAs" :t="t" @changed="(effective) => emit('modulesChanged', effective)" />
          <PublicPagePanel
            v-if="!modules || modules.includes('public-menu')"
            :api="api"
            :me="signedInAs"
            :t="t"
            :bookings="!modules || modules.includes('reservations')"
          />
          <VenueScreen :api="api" :me="signedInAs" :locale="locale" :t="t" />
          <DevicesPanel :api="api" :me="signedInAs" :t="t" v-bind="venue ? { timeZone: venue.timezone } : {}" />
          <PohodaPanel v-if="!modules || modules.includes('accounting')" :api="api" :me="signedInAs" :t="t" />
        </template>

        <SettingsScreen
          v-if="screen === 'settings' || (screen === 'staff' && !api)"
          :section="screen === 'staff' ? 'staff' : 'venue'"
          :venue="venue"
          :devices="devices"
          :staff="staff"
          :locale="locale"
          :t="t"
          @locale="(value) => $emit('locale', value)"
        />
      </div>
    </main>
  </div>
</template>

<!--
  THE UNSCOPED `gf-*` BLOCK THAT USED TO SIT BELOW THIS ONE HAS MOVED TO
  `@gf/ui/office.css`, imported by `main.ts`.

  Its reasoning was right and is preserved there: nine screens share one table,
  one panel and one chip, and scoping them would mean nine copies of the same
  forty lines which drift apart by the third screen. What was wrong was the
  ADDRESS. Living inside this component put it out of reach of the till, the
  kitchen screen and the guest's phone — which between them had 23 more
  definitions of a primary button, and no chip, panel or table at all.

  Four of the nine screens in this app (`TodayScreen`, `SalesScreen`,
  `KitchenScreen`, `FiscalScreen`) still have no `<style>` block of their own.
  They are compositions of those classes, and they re-skin from the shared file.
-->
<style scoped>
/*
  MYMONDAY'S FRAME (2026-09-25): a sidebar on the darker `canvas` behind a page
  on the lighter `surface`, one hairline between them, and a quiet 48 px bar
  across the top of the page. It was the reverse — a white sidebar beside a
  grey page — which put the navigation in front of the content it serves.
*/
.shell {
  display: grid;
  grid-template-columns: 15rem minmax(0, 1fr);
  min-height: 100vh;
  background: var(--gf-color-surface);
  /* Native checkboxes and radios in ink, not the browser's bright blue — inherited. */
  accent-color: var(--gf-color-text);
}

/* Sticky, so the navigation stays put while a long register scrolls. */
.shell__nav {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-0);
  height: 100vh;
  padding: 0 var(--gf-space-2) var(--gf-space-2);
  overflow-y: auto;
  background: var(--gf-color-canvas);
  border-right: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.shell__brand {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
  min-height: 48px;
  padding: 0 var(--gf-space-2);
}

.shell__brand img {
  flex: none;
  border-radius: var(--gf-radius-sm);
}

.shell__brandText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.shell__venue {
  overflow: hidden;
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.shell__product {
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

/* The search trigger reads as a nav item that happens to open a dialog. */
.shell__palette {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
  min-height: 32px;
  margin-bottom: var(--gf-space-3);
  padding: 0 var(--gf-space-2);
  font: inherit;
  font-size: var(--gf-text-base);
  text-align: left;
  color: var(--gf-color-text-muted);
  background: transparent;
  border: 0;
  border-radius: var(--gf-radius-md);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

.shell__paletteLabel {
  flex: 1;
}

@media (hover: hover) {
  .shell__palette:hover {
    color: var(--gf-color-text);
    background: var(--gf-color-surface-active);
  }
}

.shell__groups {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
}

.shell__group {
  margin: 0;
  padding: 0 var(--gf-space-2) var(--gf-space-1);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

.shell__items {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.shell__item {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  width: 100%;
  min-height: 32px;
  padding: 0 var(--gf-space-2);
  font: inherit;
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-medium);
  text-align: left;
  color: var(--gf-color-text-muted);
  background: transparent;
  border: 0;
  border-radius: var(--gf-radius-md);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

.shell__icon {
  flex: none;
}

.shell__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

@media (hover: hover) {
  .shell__item:hover {
    color: var(--gf-color-text);
    background: color-mix(in srgb, var(--gf-color-surface-active) 60%, transparent);
  }
}

.shell__item:focus-visible,
.shell__palette:focus-visible,
.shell__signOut:focus-visible,
.shell__navClose:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: calc(var(--gf-focus-width) * -1);
}

/*
  The current screen is marked with `aria-current` — announced, not only drawn —
  as a neutral pressed-in fill and the full text colour, Monday's way. It was
  the brand red on a pink tint: with that red now the fill of the one action
  that matters, a red nav item would compete with it on every screen.
*/
.shell__item[aria-current="page"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface-active);
}

.shell__item--preview {
  color: var(--gf-color-text-muted);
}


.shell__who {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
  margin-top: auto;
  padding: var(--gf-space-4) var(--gf-space-2) 0;
}

.shell__whoName {
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.shell__whoName strong {
  color: var(--gf-color-text);
  font-weight: var(--gf-weight-semibold);
}

.shell__signOut {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  min-height: 32px;
  margin: 0 calc(var(--gf-space-2) * -1);
  padding: 0 var(--gf-space-2);
  font: inherit;
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-medium);
  text-align: left;
  color: var(--gf-color-text-muted);
  background: transparent;
  border: 0;
  border-radius: var(--gf-radius-md);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .shell__signOut:hover {
    color: var(--gf-color-text);
    background: var(--gf-color-surface-active);
  }
}

.shell__main {
  min-width: 0;
  background: var(--gf-color-surface);
}

.shell__header {
  position: sticky;
  top: 0;
  z-index: var(--gf-z-sticky);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2) var(--gf-space-4);
  min-height: 48px;
  padding: var(--gf-space-1) var(--gf-space-4) var(--gf-space-1) var(--gf-space-6);
  background: var(--gf-color-surface);
  border-bottom: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.shell__crumbs {
  display: flex;
  align-items: baseline;
  gap: var(--gf-space-2);
  min-width: 0;
}

.shell__crumb,
.shell__sep {
  font-size: var(--gf-text-base);
  color: var(--gf-color-text-muted);
  white-space: nowrap;
}

.shell__title {
  margin: 0;
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-semibold);
  line-height: var(--gf-leading-tight);
  letter-spacing: -0.005em;
}

.shell__tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gf-space-3);
}

/*
  A segmented control, not four outlined buttons that happen to be adjacent:
  MyMonday's — a sunken track, and the chosen period lifted out of it onto the
  surface. There used to be TWO `[aria-pressed]` rules here, and the second one
  silently painted the chosen period solid ink over the first.
*/
.shell__range {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--gf-color-surface-sunken);
  border-radius: var(--gf-radius-md);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle);
}

.shell__rangeOption {
  min-height: 28px;
  padding: 0 var(--gf-space-3);
  font: inherit;
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  border: 0;
  border-radius: var(--gf-radius-sm);
  background: transparent;
  color: var(--gf-color-text-muted);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out),
    box-shadow var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .shell__rangeOption:hover {
    color: var(--gf-color-text);
  }
}

.shell__rangeOption[aria-pressed="true"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  box-shadow:
    0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle),
    var(--gf-shadow-sm);
}

.shell__rangeOption:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.shell__custom {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.shell__date {
  min-height: 32px;
  padding: 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
}

.shell__content {
  padding: var(--gf-space-6) var(--gf-space-7) var(--gf-space-8);
}

/* The phone-only controls, absent from a desktop's layout and its accessibility tree. */
.shell__navClose,
.shell__barButton,
.shell__scrim {
  display: none;
}

.shell__barButton,
.shell__navClose {
  align-items: center;
  justify-content: center;
  flex: none;
  width: var(--gf-target-min);
  height: var(--gf-target-min);
  padding: 0;
  font: inherit;
  color: var(--gf-color-text);
  background: transparent;
  border: 0;
  border-radius: var(--gf-radius-md);
  cursor: pointer;
}

.shell__barButton:active,
.shell__navClose:active {
  background: var(--gf-color-surface-active);
}

.shell__barButton:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

@media (max-width: 60rem) {
  .shell {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Closed: not drawn, not reachable, not announced. */
  .shell__nav {
    display: none;
  }

  /* Open: a sheet over the page from the left, on its own scrim. */
  .shell--navOpen .shell__nav {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: calc(var(--gf-z-overlay) + 1);
    display: flex;
    width: min(18rem, 86vw);
    height: 100dvh;
    box-shadow: var(--gf-shadow-lg);
    animation: shell-sheet-in var(--gf-motion-base) var(--gf-ease-out);
  }

  .shell__scrim {
    position: fixed;
    inset: 0;
    z-index: var(--gf-z-overlay);
    display: block;
    background: var(--gf-color-scrim);
  }

  .shell__navClose {
    display: inline-flex;
    margin-left: auto;
  }

  /* A thumb, not a cursor: 32 px rows are a desktop's. */
  .shell__item,
  .shell__palette,
  .shell__signOut {
    min-height: var(--gf-target-min);
  }

  .shell__barButton {
    display: inline-flex;
  }

  /* 48 px: the menu, the screen's name, search. The venue is inside the sheet. */
  .shell__header {
    gap: 0 var(--gf-space-1);
    padding: 0 var(--gf-space-1);
  }

  .shell__crumbs {
    flex: 1;
  }

  .shell__crumb,
  .shell__sep {
    display: none;
  }

  .shell__title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* The period control on a row of its own, scrolling sideways rather than wrapping. */
  .shell__tools {
    order: 3;
    flex: 1 0 100%;
    flex-wrap: nowrap;
    padding: 0 var(--gf-space-2) var(--gf-space-2);
    overflow-x: auto;
  }

  .shell__range,
  .shell__custom {
    flex: none;
  }

  .shell__rangeOption {
    white-space: nowrap;
  }

  .shell__content {
    padding: var(--gf-space-5) var(--gf-space-4);
  }
}

/* "⌘K" on a device with no keyboard describes a key that is not there. */
@media (hover: none) and (pointer: coarse) {
  .shell__palette .gf-kbd {
    display: none;
  }
}

@keyframes shell-sheet-in {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .shell__item,
  .shell__palette,
  .shell__signOut,
  .shell__rangeOption {
    transition: none;
  }

  .shell--navOpen .shell__nav {
    animation: none;
  }
}
</style>
